import { useTranslation } from 'react-i18next';
import { activePlayerIcon, pendingPlayerIcon } from '@/assets';
import type { PlayerPresence } from 'shared-types';
import { createTextStroke } from '@/styles/textStroke';
import { PillButton } from '@/shared/components/PillButton/PillButton';

const headingStyle = createTextStroke('2.5px');
const playerNameStyle = {
  ...createTextStroke('1.5px'),
  textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
};

export function DialogHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-center font-mind-boggle text-xl text-[#F2F2F2]" style={headingStyle}>
      {children}
    </h3>
  );
}

interface PlayerInvitationSectionProps {
  players?: PlayerPresence[];
}

export function PlayerInvitationSection({ players = [] }: PlayerInvitationSectionProps) {
  const { t } = useTranslation();
  const pendingCount = Math.max(0, 4 - players.length);

  return (
    <div className="flex flex-col gap-3">
      <DialogHeading>{t('lobby.playerInvitations')}</DialogHeading>
      <div className="flex gap-4 justify-center">
        {/* Render active player icons with names */}
        {players.slice(0, 4).map((player) => (
          <div key={player.userId} className="flex flex-col items-center gap-1 w-14">
            <img src={activePlayerIcon} alt="" className="w-12 h-12 flex-shrink-0" />
            <span
              className="text-xs font-bold text-white w-full text-center truncate"
              style={playerNameStyle}
            >
              {player.username ?? t('common.player')}
            </span>
          </div>
        ))}
        {/* Render pending player icons */}
        {Array.from({ length: pendingCount }, (_, i) => (
          <div key={`pending-${String(i)}`} className="flex flex-col items-center gap-1 w-14">
            <img src={pendingPlayerIcon} alt="" className="w-12 h-12 flex-shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
}

interface CountdownSectionProps {
  countdown: number;
  isCreator?: boolean;
  onStartNow?: () => void;
}

export function CountdownSection({
  countdown,
  isCreator = false,
  onStartNow
}: CountdownSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3">
      <DialogHeading>{t('lobby.gameStart')}</DialogHeading>
      <div className="flex flex-col items-center gap-4 py-4">
        {/* Start Now button with countdown - only show for room creator */}
        {isCreator ? (
          <PillButton
            onClick={onStartNow}
            variant="green"
            className="w-56 h-14"
            icon={
              <span
                className="font-mind-boggle text-[#F2F2F2] leading-none text-sm whitespace-nowrap"
                style={{ fontWeight: 1000, ...createTextStroke('1.5px') }}
              >
                {t('lobby.startNow')} ({countdown})
              </span>
            }
          >
            {t('lobby.startNow')} ({countdown})
          </PillButton>
        ) : (
          /* Countdown display for non-creators */
          <div className="flex flex-col items-center gap-2">
            <div className="text-6xl font-bold text-white" style={createTextStroke('3px')}>
              {countdown}
            </div>
            <p className="text-center text-white text-sm font-semibold">
              {t('lobby.countdownText', { count: countdown })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
